// Data layer for the Newfi Split eDocs monitor. Reuses the env-aware
// fetchWithFallback helper from OrgUsageAnalytics so prod/dev routing, the
// double `/admin` prefix and the userId query param behave identically to
// every other admin page.
import { fetchWithFallback } from "../OrgUsageAnalytics/api";

const BASE = "/newfi-split-edocs";

/**
 * The filter vocabulary — phases, trace phases/types/levels, package kinds and
 * channels, and every trace code with its description.
 *
 * Fetched rather than hard-coded on purpose: the backend owns the code
 * registry, and a dropdown that drifts from it silently hides traces.
 */
export const fetchFilterOptions = (env) =>
  fetchWithFallback(`${BASE}/filters`, {}, { env, timeoutMs: 20000 });

/**
 * Package counts by phase, plus how many carry an error trace.
 *
 * Same 45s budget as the list. It used to be 30s, which meant that on a cold
 * `admin` instance the counts were the one request that aborted while the list
 * beside them survived — so the strip vanished and nothing said why.
 */
export const fetchStats = ({ organizationId } = {}, env) =>
  fetchWithFallback(
    `${BASE}/stats`,
    { organizationId },
    { env, timeoutMs: 45000 },
  );

/**
 * One page of packages, newest first.
 *
 * `cursor` is opaque — pass back the `nextCursor` you were handed, never build
 * one.
 */
export const fetchPackages = (params = {}, env) =>
  fetchWithFallback(`${BASE}/packages`, params, { env, timeoutMs: 45000 });

/** One package: record, steps, claim state, push ledger, first page of traces. */
export const fetchPackageDetail = (recordId, params = {}, env) =>
  fetchWithFallback(
    `${BASE}/packages/${encodeURIComponent(recordId)}`,
    params,
    { env, timeoutMs: 45000 },
  );

/** One package's trail, paginated. */
export const fetchPackageTraces = (recordId, params = {}, env) =>
  fetchWithFallback(
    `${BASE}/packages/${encodeURIComponent(recordId)}/traces`,
    params,
    { env, timeoutMs: 45000 },
  );

/** Traces across every package — what is failing right now, anywhere. */
export const fetchTraces = (params = {}, env) =>
  fetchWithFallback(`${BASE}/traces`, params, { env, timeoutMs: 45000 });

/**
 * Every feature flag the backend registry exposes, with its stored value,
 * default and whether the feature it gates is currently active.
 *
 * The list is fetched, never hard-coded here: the backend owns the registry, so
 * a flag added there appears in this page without a frontend deploy — and a
 * flag missing from it cannot be written at all.
 */
export const fetchFeatureFlags = (env) =>
  fetchWithFallback("/feature-flags", {}, { env, timeoutMs: 20000 });

/**
 * Set one flag. Returns the updated row so the UI renders what the backend
 * actually stored rather than what it optimistically assumed.
 */
export const updateFeatureFlag = ({ flagName, value }, env) =>
  fetchWithFallback(
    "/feature-flags",
    {},
    {
      env,
      timeoutMs: 20000,
      method: "POST",
      body: { flagName, value },
    },
  );

/**
 * The eDoc poll's first-layer loan filter — which loan numbers the 5-minute
 * poll may act on at all.
 *
 * Separate from the flags above because it is not a boolean: the flag decides
 * whether the poll runs, this decides what it is allowed to see. A 500 here is
 * meaningful and must not be rendered as "no loans allowed" — an unreadable
 * filter and a deliberately empty one look identical to a reader and mean
 * opposite things to an operator.
 */
export const fetchPollLoanFilter = (env) =>
  fetchWithFallback(
    "/feature-flags/newfi-split-edoc-poll-filter",
    {},
    { env, timeoutMs: 20000 },
  );

/** Set the mode and the loan-number list in one write. */
export const updatePollLoanFilter = ({ mode, loanNumbers }, env) =>
  fetchWithFallback(
    "/feature-flags/newfi-split-edoc-poll-filter",
    {},
    {
      env,
      timeoutMs: 20000,
      method: "POST",
      body: { mode, loanNumbers },
    },
  );
